import { useQuery } from '@tanstack/react-query';

import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export interface CostBreakdown {
  labor?: number | null;
  materials?: number | null;
  permits?: number | null;
  equipment?: number | null;
  other?: number | null;
}

export interface SeasonalInsight {
  best_months: string[];
  savings_potential?: string;
  reason?: string;
}

export interface PriceOutlierWarning {
  message: string;
}

export interface MaterialCost {
  material_name: string;
  current_price: string;
  trend: string;
  last_updated: string;
}

export interface DiyVsContractor {
  diy_cost_low: number;
  diy_cost_high: number;
  contractor_cost_low: number;
  contractor_cost_high: number;
  savings_amount: number;
  savings_percentage: string;
  diy_time_estimate: string;
  skill_level_required: string;
  notes?: string;
}

export interface CostEstimateData {
  low: number;
  typical: number;
  high: number;
  currency: string;
  unit: string;
  confidence: string;
  notes?: string;
  factors?: string[];
  cost_breakdown?: CostBreakdown;
  seasonal_insight?: SeasonalInsight;
  price_outlier_warning?: PriceOutlierWarning;
  material_costs?: MaterialCost[];
  diy_vs_contractor?: DiyVsContractor;
}

export interface CostEstimateResponse {
  success: boolean;
  data: CostEstimateData | null;
  message?: string;
}

interface EnrichmentParams {
  query: string;
  service_type: string;
  location: string;
  requirements?: string;
}

export const useGetCostEstimate = (
  params: EnrichmentParams,
  enabled = false // Start disabled, enable when tab is focused
) => {
  const { session } = useAuth();

  return useQuery({
    queryKey: ['cost-estimate', params.query, params.service_type, params.location],
    queryFn: async () =>
      handleApiCall<CostEstimateResponse, EnrichmentParams>(
        session,
        'POST',
        'vendor/enrichments/cost-estimate',
        params
      ),
    enabled,
    staleTime: 60 * 60 * 1000, // Cache for 1 hour
    retry: 1,
  });
};
